/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField, Grid, Snackbar, Alert, FormControlLabel, FormHelperText, Checkbox } from "@mui/material";

import { useAppContext } from "../core/AppContext";
import * as Yup from "yup";
import { useAdAccountService } from "./adAccountService";
import { useSiteService } from "../sites/siteService";
import { useTenantService } from "../tenants/tenantService";
import MultiselectOption from "../shared/MulitselectOption";
import { AppRoleAssignment, User } from "microsoft-graph";
import { convertListToString, convertStringToList } from "./utils";
import { AllowedDomains, AppName, AppNames, AppRoleValues, AppServicePrincipals, BITMESH_TENANT } from "../core/AppConfig";
import { initUserProfileService, UserProfile } from "../security/userProfileService";
import { initUserAccountService } from "../security/userAccountService";
import { FormContainer, FormHeader, FormSection } from "../shared/FormLayout";
import { ProfilePicker } from "../shared/ProfilePicker";

type Props = {
  adAccount: User | null;
  adAccountRoleAssignments: AppRoleAssignment[] | null;
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export const EditAdAccountForm: FC<Props> = ({ adAccount, adAccountRoleAssignments }) => {
  const app = useAppContext();
  const adAccountService = useAdAccountService();
  const profileService = initUserProfileService();
  const accountService = initUserAccountService();
  const siteService = useSiteService();
  const tenantService = useTenantService();

  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const [tenants, setTenants] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<UserProfile[]>([]);

  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [selectedSites, setSelectedSites] = useState<any[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<any[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string|null>(null);
  const [savedProfileId, setSavedProfileId] = useState<string|null>(null);
  const [selectedApplications, setSelectedApplications] = useState<any[]>([]);
  const [providedApplications, setProvidedApplications] = useState<any[]>([]);

  useEffect(() => {
    tenantService.getAll().then(setTenants);
    siteService.getAll().then(sites => {
      setSites(sites);
      setSiteOptions(sites);
    });
    profileService.getUserProfiles().then(setProfiles);
  }, [app.user]);

  const tenantsDefault =
    convertStringToList(
      adAccount?.onPremisesExtensionAttributes?.extensionAttribute1 as string
    ) ?? [];
  const sitesDefault =
    convertStringToList(
      adAccount?.onPremisesExtensionAttributes?.extensionAttribute2 as string
    ) ?? [];

  useEffect(() => {
    if (adAccount) {
      setSelectedTenants(tenantsDefault);
      setSelectedSites(sitesDefault);
      adAccount?.id && accountService.getUserFor(adAccount?.id)
        .then(account => {
          setSelectedProfile(account.userProfileId);
          setSavedProfileId(account.userProfileId);
        });
    }
  }, [adAccount]);

  useEffect(() => {
    if (adAccountRoleAssignments) {
      const existingApplications = adAccountRoleAssignments.map(userRoleAssignment => {
        return AppNames.find((appName: AppName) => {
          const { servicePrincipalId, appRoleId } = AppServicePrincipals[appName];
          return servicePrincipalId === userRoleAssignment.resourceId 
            && (appRoleId === userRoleAssignment.appRoleId || userRoleAssignment.appRoleId === EMPTY_GUID);
        });
      });
      setSelectedApplications(existingApplications);
      setProvidedApplications(existingApplications);
    }
  }, [adAccountRoleAssignments]);

  const validationSchema = Yup.object().shape({
    userPrincipalName: Yup.string().required("Brukernavn er påkrevd")
      .test('test-domain', 'Domenet støttes ikke',
      value => AllowedDomains.some(domain => value?.endsWith(domain))),
    displayName: Yup.string().required("Visningsnavn er påkrevd"),
    ...(!adAccount
      ? {
        password: Yup.string().required("Passord er påkrevd")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
          "Minst 8 tegn, med stor og liten bokstav, tall og spesialtegn"
        ),
      }
      : {}),
    applications: Yup.string().required("Velg minst én applikasjon"),
    tenants: Yup.string().required("Velg minst én tenant"),
    sites: Yup.string().required("Velg minst én site"),
  });
  const formOptions = {
    defaultValues: {
      displayName: adAccount?.displayName ?? "",
      userPrincipalName: adAccount?.userPrincipalName ?? "",
      forceChangePassword: true,
      tenants: tenantsDefault.join(","),
      sites: sitesDefault.join(","),
    },
    resolver: yupResolver(validationSchema),
  };

  // get functions to build form with useForm() hook
  const { register, handleSubmit, formState, setValue, clearErrors } =
    useForm(formOptions);
  const { errors, isSubmitting } = formState;

  useEffect(() => {
    clearErrors('applications');
    setValue("applications", `${selectedApplications.join(",")}`);
  }, [selectedApplications]);

  useEffect(() => {
    clearErrors('tenants');
    setValue("tenants", `${selectedTenants.join(",")}`);
    setSiteOptions(sites.filter(site => selectedTenants.includes(BITMESH_TENANT) || selectedTenants.includes(site.tenantId)));
  }, [selectedTenants]);

  useEffect(() => {
    clearErrors('sites');
    setValue("sites", `${selectedSites.join(",")}`);
  }, [selectedSites]);

  async function onSubmit({ userPrincipalName, password, displayName, forcePasswordChange }: any) {
    setErrorMessage(null);
    try {
      if (!adAccount) {
        var result = await adAccountService.addAdAccount(
          userPrincipalName,
          displayName as string,
          password,
          convertListToString(selectedTenants),
          convertListToString(selectedSites),
          selectedApplications,
          forcePasswordChange,
          selectedProfile || null
        );
        if (!selectedProfile && result?.id) {
          await profileService.addOrUpdateUserProfileFor(result.id, {
            name: displayName as string,
            tenantIds: selectedTenants,
            siteIds: selectedSites,
            roles: selectedApplications.map((app: AppName) => AppRoleValues[app]),
            isPublic: false,
            permissions: [],
          });
        }
      } else {
        const newApplications = selectedApplications
          .filter(app => !providedApplications.includes(app));
        const removedAppRoleAssignments = providedApplications
          .filter(app => !selectedApplications.includes(app))
          .map((app: AppName) => {
            return adAccountRoleAssignments?.find(userRoleAssignment => {
              const { servicePrincipalId, appRoleId } = AppServicePrincipals[app];
              return servicePrincipalId === userRoleAssignment.resourceId && appRoleId === userRoleAssignment.appRoleId;
            }) as AppRoleAssignment;
          });
        await adAccountService.updateAdAccount(
          adAccount?.id as string,
          userPrincipalName as string,
          displayName as string,
          convertListToString(selectedTenants),
          convertListToString(selectedSites),
          newApplications,
          removedAppRoleAssignments,
          forcePasswordChange,
          selectedProfile || null
        );
      }
      setSavedProfileId(selectedProfile);
      setSuccessOpen(true);
    } catch (e: any) {
      const graphError = e?.response?.data?.error;
      const message = graphError?.message || e?.response?.data?.message || e?.message || "An unexpected error occurred";
      setErrorMessage(message);
    }
  }

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <FormHeader section="AD-kontoer" title={adAccount?.displayName || "Ny AD-konto"} isSubmitting={isSubmitting} />

      <FormSection title="Konto">
        <Grid item xs={12} md={6}>
          <TextField
            label="Visningsnavn"
            size="small"
            fullWidth
            {...register("displayName")}
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Brukernavn (UPN)"
            size="small"
            fullWidth
            {...register("userPrincipalName")}
            disabled={!!adAccount}
            error={!!errors.userPrincipalName}
            helperText={errors.userPrincipalName?.message}
          />
        </Grid>
        {!adAccount && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                label="Passord"
                type="password"
                size="small"
                fullWidth
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Checkbox defaultChecked={formOptions.defaultValues.forceChangePassword} {...register('forcePasswordChange')} />}
                label="Tving passordbytte ved første innlogging"
              />
            </Grid>
          </>
        )}
      </FormSection>

      <FormSection title="Tilgang">
        <Grid item xs={12} md={6}>
          <MultiselectOption
            variant="outlined"
            size="small"
            dense
            fullWidth
            label="Applikasjoner"
            values={selectedApplications}
            onChange={setSelectedApplications}
            items={AppNames.map((app) => ({ label: app, value: app }))}
            selectAllLabel="Alle applikasjoner"
            renderSelectedValues={(values: any) =>
              values.length === AppNames.length ? "Alle applikasjoner" : values.join(", ")
            }
          />
          {errors.applications && <FormHelperText error>{errors.applications.message}</FormHelperText>}
        </Grid>
        <Grid item xs={12} md={6}>
          <MultiselectOption
            variant="outlined"
            size="small"
            dense
            fullWidth
            label="Tenants"
            values={selectedTenants}
            onChange={setSelectedTenants}
            items={tenants.map((tenant) => ({ label: tenant.name, value: tenant.id }))}
            selectAllLabel="Alle tenants"
            renderSelectedValues={(values: any) =>
              values.length === tenants.length
                ? "Alle tenants"
                : values.map((value: any) => tenants.find((tenant) => tenant.id === value)?.name).join(", ")
            }
          />
          {errors.tenants && <FormHelperText error>{errors.tenants.message}</FormHelperText>}
        </Grid>
        <Grid item xs={12}>
          <MultiselectOption
            variant="outlined"
            size="small"
            dense
            fullWidth
            label="Sites"
            values={selectedSites}
            onChange={setSelectedSites}
            items={siteOptions.map((site) => ({ label: site.name, value: site.id }))}
            selectAllLabel="Alle sites"
            renderSelectedValues={(values: any) =>
              values.length === siteOptions.length
                ? "Alle sites"
                : values.map((value: any) => sites.find((site) => site.id === value)?.name).join(", ")
            }
          />
          {errors.sites && <FormHelperText error>{errors.sites.message}</FormHelperText>}
        </Grid>
      </FormSection>

      <FormSection title="Profil">
        <Grid item xs={12}>
          <ProfilePicker
            profiles={profiles}
            selected={selectedProfile}
            onSelect={setSelectedProfile}
            savedProfileId={adAccount ? savedProfileId : undefined}
          />
        </Grid>
      </FormSection>

      <Snackbar open={successOpen} autoHideDuration={3000} onClose={() => setSuccessOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: '100%' }}>
          Kontoen er {adAccount ? 'oppdatert' : 'opprettet'}.
        </Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={10000} onClose={() => setErrorMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </FormContainer>
  );
};
